/* ----------------- React --------------- */
import { useState } from "react";

/* ----------------- Third parties --------------- */
import { useRouter } from "expo-router";
import { HelperText, TextInput } from "react-native-paper";

/* ----------------- Hooks --------------- */
import { useAppTheme } from "@hooks/useAppTheme";

/* ----------------- Constants --------------- */
import { events } from "@constants/tracking";

/* ----------------- UI --------------- */
import { Box, useToast } from "@memorang/ui";

/* ----------------- Components --------------- */
import DeleteFormActions from "./DeleteFormActions";

import { useAuth } from "@clerk/clerk-expo";
/* ----------------- Mutations --------------- */
import { zodResolver } from "@hookform/resolvers/zod";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import useClerkUser from "@hooks/useClerkUser";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { deleteAccount } from "../api/mutations";

type Props = {
	handleSendFeedback: () => void;
	handleDismiss: () => void;
};

const DeleteForm = ({ handleSendFeedback, handleDismiss }: Props) => {
	const { email: existingUserEmail } = useClerkUser();
	const { signOut } = useAuth();

	const router = useRouter();

	const { showToast } = useToast();

	const { t } = useTranslation();

	const emailSchema = z.object({
		email: z.string().email(t("dialog.delete.confirmdelete.validEmail")),
	});
	const {
		control,
		handleSubmit,
		watch,
		formState: { errors },
		setValue,
	} = useForm({
		resolver: zodResolver(emailSchema),
		mode: "onChange",
	});

	const email = watch("email");
	const { explicitCallTrackCustomEvent } = useAnalytics();

	const [showError, setShowError] = useState(false);
	const [loading, setLoading] = useState(false);

	const { userId } = useClerkUser();
	const tenantId = useAppStore((store) => store.tenant.id);

	const handleLogout = async () => {
		await signOut();
		handleDismiss();
		router.replace("/");
	};

	const handleDelete = async () => {
		setLoading(true);
		const finalEmail = email.trim().toLowerCase();
		if (finalEmail !== existingUserEmail) {
			setShowError(true);
			setLoading(false);
			return;
		}
		if (userId && tenantId) {
			try {
				await deleteAccount({ userId, tenantId });
				explicitCallTrackCustomEvent({
					eventName: events.userDeleted,
					user_id: userId,
					user_email: email,
					tenant_id: tenantId,
				});
				showToast({
					message: t("dialog.delete.confirmdelete.successMessage"),
				});
				handleSendFeedback();
				await handleLogout();
			} catch (err) {
				setShowError(true);
				setValue("email", "");
				console.error("Error deleting account:", err);
			} finally {
				setLoading(false);
			}
		}
	};

	const handleFormAction = (shouldDelete?: boolean) => {
		if (shouldDelete) {
			handleDelete();
		} else {
			handleDismiss();
		}
	};

	const theme = useAppTheme();

	const errorMessage = t("dialog.delete.confirmdelete.errorMessage");
	const emailError =
		errors.email && typeof errors.email.message === "string"
			? errors.email.message
			: null;
	const isInputError = emailError || showError;
	return (
		<>
			<Controller
				control={control}
				render={({ field: { onChange, onBlur, value } }) => (
					<Box>
						<TextInput
							mode="outlined"
							placeholder={`${t("common.eg")}, dave@gmail.com`}
							onChangeText={(text) => {
								onChange(text);
								setShowError(false);
							}}
							onBlur={onBlur}
							value={value}
							autoFocus
							placeholderTextColor={theme.colors.onSurfaceDisabled}
							outlineStyle={{
								backgroundColor: "transparent",
							}}
							autoCapitalize="none"
							onSubmitEditing={handleSubmit(() => handleFormAction(true))}
							keyboardType="email-address"
							disabled={loading}
							style={{ width: "100%" }}
							error={!!isInputError}
						/>
						{emailError && <HelperText type="error">{emailError}</HelperText>}
						{showError && !errors.email && (
							<CustomMarkdownRenderer
								text={errorMessage}
								customStyles={{
									text: {
										fontSize: 12,
										color: theme.colors.error,
									},
								}}
							/>
						)}
						<DeleteFormActions
							handleAction={handleFormAction}
							primaryActionLabel={t("dialog.delete.confirmdelete.cta")}
							disablePrimaryButton={!email || loading}
							loadingPrimaryButton={loading}
						/>
					</Box>
				)}
				name="email"
				defaultValue=""
			/>
		</>
	);
};

export default DeleteForm;
